// 그래픽 효과 품질(저사양 토글) — 파티클·postFX 부담을 줄임. localStorage 저장.
// 캔버스(Phaser) 모션은 CSS prefers-reduced-motion이 닿지 않으므로 여기서 직접 반영한다
// (반딧불·꽃잎·카메라 postFX·캐릭터 흔들림). 영유아 시각안전 + 모션 민감 접근성.
const KEY = "gc_fx_low";

interface NavExt {
  deviceMemory?: number;
}

/**
 * 저사양(가벼운 그래픽) 여부 결정(순수·테스트 가능).
 * 우선순위: 사용자 명시 선택 > OS '모션 줄이기' > 기기 성능 휴리스틱.
 *
 * 🔴 **이 값으로 CSS 애니메이션을 끄면 안 된다** — `reduceMotionWanted` 를 쓸 것 (2026-09-27).
 * 기기 휴리스틱(`메모리 ≤4GB 또는 코어 ≤4`)은 폰·태블릿 대부분에 참이다(아이폰·아이패드는 메모리를 아예
 * 안 알려 주고, 안드로이드 보급형은 4GB·4코어다). 이 값이 그대로 `data-reduce-motion` 이 되어 **모든** CSS
 * 애니메이션을 껐고, 그래서 **폰·태블릿에서 전자책 마법 층이 하나도 안 움직였다**(사용자 신고). 휴리스틱의
 * 목적은 체험공간의 무거운 효과(파티클·postFX)를 줄이는 것이지, 손그림 띠를 멈추는 것이 아니다.
 */
export function decideFxLow(input: {
  stored?: string | null;
  reducedMotion?: boolean;
  deviceMemory?: number;
  hardwareConcurrency?: number;
}): boolean {
  if (input.stored === "1") return true; // 사용자가 직접 켬 — 최우선
  if (input.stored === "0") return false; // 사용자가 직접 끔 — OS설정보다 우선
  if (input.reducedMotion) return true; // OS 모션 줄이기 → 차분한 월드(a11y·시각안전)
  const dm = input.deviceMemory;
  const hc = input.hardwareConcurrency;
  return (typeof dm === "number" && dm <= 4) || (typeof hc === "number" && hc <= 4);
}

/**
 * 🎬 **화면 움직임(CSS 애니메이션)을 멈춰야 하나** — 사용자가 직접 켰거나 OS 가 「동작 줄이기」일 때만.
 * 기기 성능은 보지 않는다(위 `decideFxLow` 주석). 마법 띠·흔들림·첫 화면 이펙트가 이것을 따른다.
 */
export function decideReduceMotion(input: { stored?: string | null; reducedMotion?: boolean }): boolean {
  if (input.stored === "1") return true;
  if (input.stored === "0") return false;
  return !!input.reducedMotion;
}

export function reduceMotionWanted(): boolean {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch {
    /* 저장소가 막힌 기기 — 고른 적 없는 것으로 */
  }
  return decideReduceMotion({ stored, reducedMotion: prefersReducedMotion() });
}

/** OS '동작 줄이기'(prefers-reduced-motion) 선호 여부. 미지원 환경은 false. */
export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

/** 저사양(가벼운 그래픽)? 저장값 → OS 모션선호 → 기기 휴리스틱 순. */
export function getFxLow(): boolean {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch {
    /* 무시 */
  }
  const nav = navigator as Navigator & NavExt;
  return decideFxLow({
    stored,
    reducedMotion: prefersReducedMotion(),
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
  });
}

export function setFxLow(low: boolean): void {
  try {
    localStorage.setItem(KEY, low ? "1" : "0");
  } catch {
    /* 무시 */
  }
}
